/**
 * One method recommender for a ranking run.
 *
 * Puts the methods in the order a host should see them: the step's own
 * recommendations first (the Event Designer's rules), reordered and annotated
 * by the room readout (`evaluateSituationRules`, #640) when a situation vector
 * is known, then every other session method that fits the item count, then the
 * ones that do not fit, each with the reason in plain words.
 *
 * Used by "Run it live" (a track's Sessions tab) and the Session app's setup
 * (`sessionAppMethodSections`). The Event Designer's ToolFraming is the one
 * older copy of this ordering left.
 */
import { recommendToolsForStep } from '@/features/session-engine-v3/engineLogic';
import { REGISTRY_SESSION_TOOL_IDS, REGISTRY_TOOL_SPECS_BY_ID } from '@/generated/registryToolSpecs';
import { evaluateSituationRules, type SituationRecommendation } from '@/lib/situationRecommendation';
import { matrixToolIdsToRegistryIds, registryIdsToMatrixToolIds } from '@/lib/situationToolIds';
import { isToolSwitchedOff } from '@/lib/switchedOffTools';
import type { SituationVector } from '@/types/prioritization/situationVector';
import type { DecisionClass } from '@/types/prioritization/session';

export interface MethodChoice {
  /** Registry id, e.g. `stack_ranking`. */
  toolId: string;
  name: string;
  description: string;
  /** Whether the item count sits inside the method's declared range. */
  fits: boolean;
  /** Why it does not fit, in plain words, when it does not. */
  fitNote?: string;
  /** Leads the list: a step recommendation or a method the room readout promoted. */
  recommended: boolean;
  /** The room readout's own words for a promotion or a caution, when it spoke. */
  why?: string;
}

export interface RecommendMethodsInput {
  itemCount: number;
  /** The room readout, when the cycle or session recorded one. */
  situationVector?: SituationVector | null;
  /** The Event Designer job whose S5 hints seed the default order. */
  jobId?: string;
  /** When known, the readout only promotes methods that fit this class. */
  decisionClass?: DecisionClass | null;
}

function fitFor(toolId: string, itemCount: number): { fits: boolean; fitNote?: string } {
  const spec = REGISTRY_TOOL_SPECS_BY_ID[toolId];
  const min = spec?.compatibility?.minItems ?? 0;
  const max = spec?.compatibility?.maxItems;
  const fits = itemCount >= min && (max == null || itemCount <= max);
  if (fits) return { fits };
  if (itemCount < min) return { fits, fitNote: `needs at least ${min} items` };
  return { fits, fitNote: `best for ${max} or fewer items` };
}

export interface MethodRecommendation {
  /** Every session method, in the order a host should see them. */
  choices: MethodChoice[];
  /** The room readout's own result, for recording the host's choice against it. */
  situation: SituationRecommendation;
}

export function recommendMethods(input: RecommendMethodsInput): MethodRecommendation {
  const { itemCount, situationVector, jobId } = input;
  const stepPicks = recommendToolsForStep('S5', itemCount, jobId);
  const stepIds = stepPicks.map((p) => p.toolId);
  const stepSet = new Set(stepIds);

  const everyMethod = REGISTRY_SESSION_TOOL_IDS.filter((id) => !isToolSwitchedOff(id));
  const byId = new Map<string, MethodChoice>();
  for (const toolId of everyMethod) {
    const spec = REGISTRY_TOOL_SPECS_BY_ID[toolId];
    byId.set(toolId, {
      toolId,
      name: spec?.displayName ?? toolId,
      description: spec?.description ?? '',
      ...fitFor(toolId, itemCount),
      recommended: stepSet.has(toolId),
    });
  }

  const situation = evaluateSituationRules({
    vector: situationVector ?? null,
    availableToolIds: registryIdsToMatrixToolIds([...byId.keys()]),
    defaultToolIds: registryIdsToMatrixToolIds(stepIds),
    decisionClass: input.decisionClass ?? null,
  });

  if (situation.applied) {
    for (const note of situation.annotations) {
      const [registryId] = matrixToolIdsToRegistryIds([note.toolId]);
      const choice = registryId ? byId.get(registryId) : undefined;
      if (!choice) continue;
      choice.why = note.text;
      if (note.kind === 'promoted') choice.recommended = true;
    }
  }

  const leadIds = situation.applied
    ? [
        ...new Set([
          ...matrixToolIdsToRegistryIds(
            situation.annotations.filter((a) => a.kind === 'promoted').map((a) => a.toolId),
          ),
          ...stepIds,
        ]),
      ]
    : stepIds;
  const restOrder = situation.applied
    ? matrixToolIdsToRegistryIds(situation.orderedToolIds)
    : [...byId.keys()];

  const ordered: MethodChoice[] = [];
  const seen = new Set<string>();
  const push = (id: string) => {
    const choice = byId.get(id);
    if (!choice || seen.has(id)) return;
    seen.add(id);
    ordered.push(choice);
  };
  // Fit is the hard rule: a recommended method the item count outgrows is shown,
  // with its reason, after every method that fits.
  const fits = (id: string) => byId.get(id)?.fits === true;
  leadIds.filter(fits).forEach(push);
  restOrder.filter(fits).forEach(push);
  [...byId.keys()].filter(fits).forEach(push);
  leadIds.forEach(push);
  restOrder.forEach(push);
  [...byId.keys()].forEach(push);
  return { choices: ordered, situation };
}

/** The first method that fits, or the first recommended one when nothing fits. */
export function pickDefaultMethod(choices: MethodChoice[]): MethodChoice | undefined {
  return choices.find((c) => c.recommended && c.fits) ?? choices.find((c) => c.fits) ?? choices[0];
}
